import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, ExternalLink, ArrowRight } from 'lucide-react';
import TiltCard from '../components/TiltCard';

const projects = [
  {
    id: 'mediainfotech',
    title: 'MediaInfoTech',
    category: 'Digital Media Platform',
    year: '2024',
    tags: ['React', 'Node.js', 'MongoDB', 'AI', 'AWS', 'CI/CD', 'Microservices'],
    img: 'https://images.unsplash.com/photo-1639094071972-a79473d1a7c6?w=700&q=80',
    url: 'https://www.mediainfotech.org',
    summary: 'Enterprise-grade media intelligence and operations platform for digital content creators and agencies.',
    overview: 'Enterprise-grade media intelligence and operations platform designed for digital content creators and agencies. The system unifies CRM, payroll, live field task tracking with location monitoring, attendance management, HR center, role-based access control (IAM-style), docket management, customer ticketing portal, internal meetings, and real-time inventory management, all within a single scalable web application.',
    challenge: 'The client needed a unified platform to manage, analyze, and distribute media content across multiple channels  replacing a patchwork of disconnected tools with a single intelligent system.',
    solution: 'We built a full-stack React + Node.js platform with a MongoDB data layer, REST APIs, real-time dashboards, and AI-powered content recommendation engine. The platform integrates with major media APIs for live data aggregation.',
    results: ['Reduced operational costs by 48%', 'Decreased daily manual workload by 80%', 'Improved accountability with real-time task and attendance visibility', 'Faster coordination across HR, operations, and customer support'],
    stack: ['React', 'Node.js', 'MongoDB', 'AI', 'AWS', 'CI/CD', 'Microservices'],
    color: '#00d4aa',
  },
  {
    id: 'biswaas',
    title: 'Biswaas Enterprise',
    category: 'E-Commerce Web Platform',
    year: '2025',
    tags: ['Web Design', 'E-Commerce', 'Branding', 'UX'],
    img: 'https://images.unsplash.com/photo-1549507805-af5113b0f247?w=700&q=80',
    url: 'https://www.biswaas.in',
    summary: 'Premium grain brand e-commerce web presence from West Bengal.',
    overview: 'Biswaas Enterprise is a West Bengal-based premium grain brand (rice, daal, atta, maida) that needed a digital presence to reach customers across West Bengal. InsightsNode designed and built their complete web platform.',
    challenge: 'Biswaas had a strong offline presence but zero digital footprint. They needed a professional web platform that reflected their commitment to quality and enabled direct online orders.',
    solution: 'We created a clean, trust-inspiring website with brand story, product catalog, supplier traceability information, and a WhatsApp-integrated ordering flow. The site is optimized for mobile users and loads under 2 seconds.',
    results: ['Launched digital presence from zero in 3 weeks', 'Mobile-first design serving 85% mobile audience', '200+ online enquiries within first month', 'Customer retention up 35% via digital engagement'],
    stack: ['React', 'Tailwind CSS', 'Netlify', 'WhatsApp API', 'Google Analytics'],
    color: '#06b6d4',
  },
  {
    id: 'gr8ideas',
    title: 'Gr8Ideas Creative',
    category: 'Agency Website & Brand System',
    year: '2024',
    tags: ['Next.js', 'Brand Design', 'UI/UX', 'Animation'],
    img: 'https://images.unsplash.com/photo-1541462608143-67571c6738dd?w=700&q=80',
    url: '#',
    summary: 'Creative agency website with interactive portfolio and lead generation.',
    overview: 'Gr8Ideas is a creative agency specializing in branding, advertising, and visual storytelling. They needed a website that matched their creative energy  bold, interactive, and built to convert visitors into clients.',
    challenge: 'As a creative agency, their website was their primary portfolio and sales tool. It needed to be visually stunning, load fast, showcase work effectively, and guide visitors to book a consultation.',
    solution: 'We built an animation-rich Next.js website with a filterable project showcase, interactive case study pages, and a conversion-optimized contact flow. The design system ensures brand consistency at all touchpoints.',
    results: ['Portfolio bounce rate reduced by 55%', 'Consultation bookings increased 3x', 'Lighthouse performance score: 96', 'Featured in 2 design showcases'],
    stack: ['Next.js', 'Framer Motion', 'Tailwind CSS', 'Vercel', 'Figma'],
    color: '#8b5cf6',
  },
  {
    id: 'neurohealth',
    title: 'NeuroHealth AI',
    category: 'Healthcare Analytics Platform',
    year: '2024',
    tags: ['Python', 'AI/ML', 'React', 'FastAPI'],
    img: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=700&q=80',
    url: '#',
    summary: 'AI-powered patient analytics and predictive diagnostic platform.',
    overview: 'NeuroHealth AI is a clinical analytics platform used by healthcare providers to predict patient risks, optimize treatment protocols, and reduce readmission rates using machine learning on patient data.',
    challenge: 'Hospital systems were drowning in data but starved of insights. Clinicians needed predictive tools that could surface high-risk patients before crises occurred  without adding to their cognitive load.',
    solution: 'We built a Python ML pipeline (scikit-learn + XGBoost) with a React dashboard featuring real-time risk scoring, cohort analysis, and explainable AI components. HIPAA-compliant data handling with full audit trails.',
    results: ['23% reduction in patient readmissions', 'High-risk patient identification accuracy: 87%', 'Adopted by 4 hospital networks', 'Saved average $2.3M per hospital annually'],
    stack: ['Python', 'scikit-learn', 'XGBoost', 'React', 'FastAPI', 'PostgreSQL'],
    color: '#6366f1',
  },
  {
    id: 'clearledger',
    title: 'ClearLedger',
    category: 'FinTech SaaS Platform',
    year: '2025',
    tags: ['SaaS', 'FinTech', 'React', 'Stripe'],
    img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=700&q=80',
    url: '#',
    summary: 'Automated financial reconciliation and reporting SaaS for SMBs.',
    overview: 'ClearLedger is a financial operations platform that automates reconciliation, expense tracking, and regulatory reporting for small and mid-size businesses. It connects directly to bank APIs and accounting software.',
    challenge: 'Finance teams at SMBs were spending 20+ hours/week on manual reconciliation. They needed an affordable, automated solution that could handle multi-entity accounting without enterprise pricing.',
    solution: 'We built a multi-tenant SaaS with bank-grade security, Plaid + QuickBooks integrations, automated reconciliation rules engine, and one-click regulatory report generation. Launched in 12 weeks.',
    results: ['95% reduction in manual reconciliation time', 'MRR: $120K within 6 months of launch', '350+ SMBs onboarded in year one', 'SOC2 Type II certified'],
    stack: ['React', 'Node.js', 'PostgreSQL', 'Stripe', 'Plaid API', 'AWS Lambda'],
    color: '#00d4aa',
  },
  {
    id: 'learnbridge',
    title: 'LearnBridge LMS',
    category: 'EdTech Learning Platform',
    year: '2024',
    tags: ['EdTech', 'React', 'Node.js', 'AI'],
    img: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=700&q=80',
    url: '#',
    summary: 'Scalable LMS for 500K+ students with live classes and AI progress tracking.',
    overview: 'LearnBridge is a full-featured Learning Management System deployed for a network of educational institutions across 3 states in India. It supports live video classes, assignments, AI adaptive learning paths, and real-time analytics for educators.',
    challenge: 'The client was running on a legacy LMS that crashed under 2,000 concurrent users. They needed a platform that could support 50K simultaneous users, integrate with 40+ existing tools, and support vernacular languages.',
    solution: 'We engineered a horizontally scalable microservices architecture on Kubernetes (GKE) with WebRTC video via Jitsi, adaptive learning AI, multi-language support for 6 Indian languages, and parent progress dashboards.',
    results: ['50K concurrent users on launch day  zero downtime', 'Student completion rate increased from 31% to 67%', 'Vernacular support added 180K new learners', 'Infrastructure cost reduced by 44% vs. legacy system'],
    stack: ['React', 'Node.js', 'WebRTC', 'Python', 'Kubernetes', 'GCP'],
    color: '#06b6d4',
  },
  {
    id: 'swiftlogix',
    title: 'SwiftLogix',
    category: 'Logistics & Supply Chain SaaS',
    year: '2025',
    tags: ['SaaS', 'IoT', 'React', 'Python'],
    img: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=700&q=80',
    url: '#',
    summary: 'Real-time fleet tracking & supply chain optimization SaaS for logistics companies.',
    overview: 'SwiftLogix is a logistics intelligence platform that provides real-time fleet tracking, route optimization, delivery prediction, and warehouse management for mid-size logistics and last-mile delivery companies across India.',
    challenge: 'A growing logistics company was managing 400+ vehicles with spreadsheets and WhatsApp. They had no visibility into delivery status, no route optimization, and were losing 18% of deliveries to delays and mis-routing.',
    solution: 'We built an IoT-connected SaaS platform with GPS tracking, ML-powered route optimization, automated customer notifications, driver app (React Native), and a management dashboard with real-time analytics.',
    results: ['Delivery delays reduced by 67%', 'Fuel costs down 22% via route optimization', 'Real-time visibility across 400+ vehicles', 'Customer satisfaction NPS improved from 31 to 74'],
    stack: ['React', 'React Native', 'Python', 'FastAPI', 'PostgreSQL', 'AWS IoT', 'Redis'],
    color: '#6366f1',
  },
  {
    id: 'terramap',
    title: 'TerraMap Analytics',
    category: 'Geospatial Intelligence Platform',
    year: '2024',
    tags: ['AI/ML', 'GIS', 'Python', 'React'],
    img: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=700&q=80',
    url: '#',
    summary: 'AI-powered geospatial analytics for urban planning and real estate intelligence.',
    overview: 'TerraMap is a geospatial intelligence platform used by urban planners, real estate developers, and government agencies to analyze land use patterns, predict property valuation, and model infrastructure impact using satellite imagery and AI.',
    challenge: 'Urban planning teams were manually analyzing satellite imagery and census data across thousands of data points  a process that took 6-8 weeks per project. They needed intelligent automation with accurate predictions.',
    solution: 'We built a Python-based geospatial ML pipeline using satellite imagery (Sentinel-2), computer vision for land classification, and a React-based GIS dashboard with interactive heatmaps, zoning overlays, and prediction models.',
    results: ['Analysis time reduced from 6 weeks to 48 hours', 'Property valuation model accuracy: 93.2%', 'Used by 3 municipal corporations', 'Processed 1.8TB satellite imagery in pilot phase'],
    stack: ['Python', 'TensorFlow', 'React', 'Mapbox GL', 'PostGIS', 'FastAPI', 'AWS S3'],
    color: '#8b5cf6',
  },
  {
    id: 'novapay',
    title: 'NovaPay Gateway',
    category: 'Payment Infrastructure Platform',
    year: '2025',
    tags: ['FinTech', 'Node.js', 'React', 'Stripe'],
    img: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=700&q=80',
    url: '#',
    summary: 'Enterprise payment orchestration platform with multi-gateway failover.',
    overview: 'NovaPay is a payment orchestration layer that sits between e-commerce platforms and multiple payment gateways (Razorpay, Stripe, PayU), routing transactions intelligently for maximum success rates and minimum cost.',
    challenge: 'A large D2C brand was losing 8.7% of transactions to payment failures. No single gateway performed reliably across all banks and UPI apps. They needed intelligent routing that could failover in under 200ms.',
    solution: 'We engineered a payment orchestration microservice with ML-based gateway routing, sub-200ms failover, real-time fraud scoring, retry logic, and a merchant analytics dashboard with reconciliation automation.',
    results: ['Transaction success rate from 91.3% to 98.6%', 'Payment processing cost reduced by 31%', 'Fraud losses reduced by 89% in 90 days', 'Handles 120K transactions/day at peak'],
    stack: ['Node.js', 'React', 'PostgreSQL', 'Redis', 'Stripe', 'Razorpay', 'AWS'],
    color: '#00d4aa',
  },
  {
    id: 'careconnect',
    title: 'CareConnect',
    category: 'Telemedicine Application',
    year: '2024',
    tags: ['Mobile', 'React Native', 'Node.js', 'WebRTC'],
    img: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=700&q=80',
    url: '#',
    summary: 'HIPAA-compliant telemedicine app with AI symptom checker and video consults.',
    overview: 'CareConnect is a telemedicine platform connecting patients with specialist doctors across India. It features AI-powered symptom triage, secure video consultations, e-prescriptions, pharmacy integrations, and longitudinal health records.',
    challenge: 'A healthcare group wanted to launch a telemedicine product to serve Tier 2/3 cities with poor specialist access. The app needed to work on low-bandwidth connections, support 10+ languages, and meet HIPAA compliance standards.',
    solution: 'We built adaptive-bitrate WebRTC video for low-bandwidth connections, offline-capable React Native app, AI symptom checker trained on Indian disease patterns, automated e-prescription generation, and ABDM health ID integration.',
    results: ['App Store rating: 4.7 / 5 (8,200+ reviews)', '95K+ consultations in first 6 months', 'Average consult connection time: 12 seconds', 'Serving 156 specialists across 34 specialties'],
    stack: ['React Native', 'Node.js', 'WebRTC', 'Python', 'TensorFlow', 'AWS', 'PostgreSQL'],
    color: '#06b6d4',
  },
  {
    id: 'agrisense',
    title: 'AgriSense IoT',
    category: 'Smart Agriculture Platform',
    year: '2025',
    tags: ['IoT', 'AI/ML', 'React', 'Python'],
    img: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=700&q=80',
    url: '#',
    summary: 'IoT-based precision agriculture monitoring with crop yield prediction AI.',
    overview: 'AgriSense is a precision agriculture platform using IoT soil sensors, weather stations, and satellite data to give farmers real-time field intelligence  soil moisture, nutrient levels, pest risk, and AI-powered crop yield forecasts.',
    challenge: 'A large cooperative of 3,000+ farmers in Maharashtra was losing 25-30% of crop yield annually to irrigation inefficiency, pest outbreaks, and suboptimal fertilizer use. They needed affordable, actionable technology at field level.',
    solution: 'We built low-cost IoT sensor nodes (soil, humidity, temperature), a LoRaWAN connectivity layer for remote fields, AI models for pest prediction and yield forecasting, and a multilingual mobile app with voice-based guidance for farmers.',
    results: ['Crop yield improved by 28% on average', 'Water usage reduced by 35% via smart irrigation', 'Pest outbreak prediction accuracy: 89%', 'Adopted by 3,200+ farmers across 5 districts'],
    stack: ['React Native', 'Python', 'TensorFlow', 'FastAPI', 'LoRaWAN', 'AWS IoT', 'PostgreSQL'],
    color: '#8b5cf6',
  },
];

const allTags = ['All', 'AI/ML', 'Web', 'FinTech', 'SaaS', 'Mobile', 'IoT', 'EdTech'];

const Work = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTag, setActiveTag] = useState('All');

  const filtered = activeTag === 'All'
    ? projects
    : projects.filter((p) =>
        p.tags.some((t) => {
          if (activeTag === 'AI/ML') return t.includes('AI') || t.includes('ML') || t.includes('Python');
          if (activeTag === 'Web') return t === 'Web Design' || t === 'Next.js' || t === 'React' || t === 'Node.js';
          if (activeTag === 'Mobile') return t === 'Mobile' || t === 'React Native';
          return t === activeTag || t.toLowerCase().includes(activeTag.toLowerCase());
        })
      );

  return (
    <main data-testid="work-page">
      {/* Hero */}
      <section
        style={{
          minHeight: '42vh',
          background: '#0a0f1e',
          display: 'flex',
          alignItems: 'flex-end',
          paddingTop: 'clamp(100px, 14vw, 130px)',
          paddingBottom: 'clamp(48px, 8vw, 64px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'radial-gradient(ellipse at 70% 30%, rgba(0,212,170,0.05) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.span className="section-label" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            // Selected Work
          </motion.span>
          <motion.h1
            style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 'clamp(2.5rem, 6vw, 5rem)', letterSpacing: '-0.04em', color: '#fff', lineHeight: 1.0, maxWidth: '700px' }}
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
          >
            Work That
            <span className="text-gradient-teal"> Speaks.</span>
          </motion.h1>
          <motion.p
            style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '1.05rem', lineHeight: 1.75, maxWidth: '480px', marginTop: '20px' }}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
          >
            11 projects across software, AI, FinTech, IoT, and more.
          </motion.p>
        </div>
      </section>

      {/* Filters */}
      <section style={{ background: '#0a0f1e', padding: 'clamp(24px, 5vw, 40px) 0 0' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-3" data-testid="project-filters">
            {allTags.map((tag) => (
              <button
                key={tag}
                onClick={() => setActiveTag(tag)}
                data-testid={`filter-${tag}`}
                style={{
                  fontFamily: 'JetBrains Mono',
                  fontSize: '0.75rem',
                  padding: '8px 20px',
                  borderRadius: '50px',
                  border: activeTag === tag ? '1px solid #00d4aa' : '1px solid rgba(255,255,255,0.1)',
                  background: activeTag === tag ? 'rgba(0,212,170,0.1)' : 'transparent',
                  color: activeTag === tag ? '#00d4aa' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  letterSpacing: '0.08em',
                  transition: 'all 0.2s ease',
                }}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section style={{ background: '#0a0f1e', padding: 'clamp(32px, 6vw, 48px) 0 clamp(80px, 10vw, 120px)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTag}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filtered.map((project, i) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.07 }}
                  onClick={() => setSelectedProject(project)}
                  data-testid={`project-card-${project.id}`}
                >
                  <TiltCard
                    className="glass-card"
                    style={{ overflow: 'hidden', cursor: 'pointer', height: '100%' }}
                    intensity={8}
                  >
                    <div style={{ height: '220px', overflow: 'hidden', position: 'relative' }}>
                      <img
                        src={project.img}
                        alt={project.title}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                        onMouseEnter={(e) => (e.target.style.transform = 'scale(1.06)')}
                        onMouseLeave={(e) => (e.target.style.transform = 'scale(1)')}
                      />
                      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${project.color}20 0%, rgba(10,15,30,0.45) 100%)` }} />
                      <span style={{
                        position: 'absolute', top: '16px', right: '16px',
                        fontFamily: 'JetBrains Mono', fontSize: '0.7rem',
                        background: 'rgba(10,15,30,0.8)', color: 'rgba(255,255,255,0.5)',
                        padding: '4px 10px', borderRadius: '20px', backdropFilter: 'blur(8px)',
                      }}>{project.year}</span>
                    </div>
                    <div style={{ padding: '28px' }}>
                      <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.7rem', color: project.color, letterSpacing: '0.1em', display: 'block', marginBottom: '8px' }}>
                        {project.category}
                      </span>
                      <h3 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '1.25rem', color: '#fff', marginBottom: '8px', letterSpacing: '-0.01em' }}>
                        {project.title}
                      </h3>
                      <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.85rem', lineHeight: 1.6, marginBottom: '16px' }}>
                        {project.summary}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {project.tags.map((t) => <span key={t} className="skill-badge">{t}</span>)}
                      </div>
                    </div>
                  </TiltCard>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
              background: 'rgba(5, 8, 18, 0.94)', backdropFilter: 'blur(16px)',
              overflowY: 'auto', padding: 'clamp(20px, 5vw, 40px) 16px',
            }}
            onClick={(e) => e.target === e.currentTarget && setSelectedProject(null)}
            data-testid="project-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 50, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.96 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              style={{
                background: '#0d1b35', border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '20px', maxWidth: 'min(92vw, 800px)', width: '100%', overflow: 'hidden',
              }}
            >
              <div style={{ height: 'clamp(180px, 38vw, 280px)', overflow: 'hidden', position: 'relative' }}>
                <img src={selectedProject.img} alt={selectedProject.title} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.55 }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, #0d1b35 25%, transparent 100%)' }} />
                <button
                  onClick={() => setSelectedProject(null)}
                  data-testid="modal-close"
                  style={{
                    position: 'absolute', top: '20px', right: '20px',
                    background: 'rgba(10,15,30,0.85)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: '50%', width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: '#fff',
                  }}
                >
                  <X size={18} />
                </button>
              </div>
              <div style={{ padding: 'clamp(20px, 4vw, 40px)' }}>
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.72rem', color: selectedProject.color, letterSpacing: '0.1em', display: 'block', marginBottom: '10px' }}>
                  {selectedProject.category}  {selectedProject.year}
                </span>
                <h2 style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: '2rem', color: '#fff', letterSpacing: '-0.03em', marginBottom: '8px' }}>
                  {selectedProject.title}
                </h2>
                {selectedProject.url !== '#' && (
                  <a href={selectedProject.url} target="_blank" rel="noopener noreferrer"
                    style={{ color: '#00d4aa', fontFamily: 'Inter', fontSize: '0.85rem', marginBottom: '24px', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {selectedProject.url} <ExternalLink size={13} />
                  </a>
                )}
                <p style={{ color: '#94a3b8', fontFamily: 'Inter', fontSize: '0.95rem', lineHeight: 1.8, marginBottom: '28px', marginTop: '16px' }}>
                  {selectedProject.overview}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div>
                    <h4 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '0.9rem', color: '#fff', marginBottom: '10px' }}>Challenge</h4>
                    <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.85rem', lineHeight: 1.7 }}>{selectedProject.challenge}</p>
                  </div>
                  <div>
                    <h4 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '0.9rem', color: '#fff', marginBottom: '10px' }}>Solution</h4>
                    <p style={{ color: '#64748b', fontFamily: 'Inter', fontSize: '0.85rem', lineHeight: 1.7 }}>{selectedProject.solution}</p>
                  </div>
                </div>
                <div style={{ marginBottom: '28px' }}>
                  <h4 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '0.9rem', color: '#fff', marginBottom: '14px' }}>Results</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedProject.results.map((r) => (
                      <div key={r} className="flex items-start gap-3">
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'rgba(0,212,170,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', color: '#00d4aa' }}>
                          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                            <path d="M1 3.5L3 5.5L8 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <span style={{ color: '#94a3b8', fontFamily: 'Inter', fontSize: '0.85rem', lineHeight: 1.6 }}>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '0.9rem', color: '#fff', marginBottom: '12px' }}>Tech Stack</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.stack.map((s) => <span key={s} className="skill-badge">{s}</span>)}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section style={{ padding: 'clamp(72px, 9vw, 100px) 0', background: '#0d1b35', textAlign: 'center' }}>
        <div className="max-w-3xl mx-auto px-6">
          <span className="section-label" style={{ display: 'block', textAlign: 'center', marginBottom: '20px' }}>// Your Project Next</span>
          <h2 style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: 'clamp(1.8rem, 4vw, 3rem)', color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: '32px' }}>
            Ready to build something
            <span className="text-gradient-teal"> remarkable?</span>
          </h2>
          <Link to="/contact" className="btn-primary" style={{ fontSize: '1rem', padding: '15px 36px' }} data-testid="work-contact-cta">
            Start a Project <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Work;
